import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { popularCompanies } from '../../data/popularCompanies';

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
`;

const SuggestionsContainer = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #eee;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 400px;
  overflow-y: auto;
  padding: 8px 0;
`;

const SuggestionsHeader = styled.div`
  padding: 16px;
  font-weight: 600;
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border);
`;

const CompanyList = styled.div`
  padding: 8px 0;
`;

const CompanyItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
  }
`;

const CompanyLogo = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: contain;
`;

const CompanyInfo = styled.div`
  flex: 1;
`;

const CompanyName = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: #333;
`;

const CompanyCategory = styled.div`
  font-size: 13px;
  color: #666;
`;

const SearchBar = ({ onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCompanies, setFilteredCompanies] = useState(popularCompanies);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFocus = () => {
    setIsOpen(true);
    setFilteredCompanies(popularCompanies); // Show all companies when focused
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Filter companies based on search term
    const filtered = popularCompanies.filter(company => 
      company.name.toLowerCase().includes(value.toLowerCase()) ||
      company.category.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCompanies(filtered);
  };

  const handleCompanySelect = (company) => {
    setSearchTerm(company.name);
    setIsOpen(false);
    onSelect(company);
  };

  return (
    <SearchContainer ref={containerRef}>
      <SearchInput
        type="text"
        placeholder="Search for a company..."
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleFocus}
      />

      {isOpen && (
        <SuggestionsContainer>
          <SuggestionsHeader>Most Popular</SuggestionsHeader>
          <CompanyList>
            {filteredCompanies.map(company => (
              <CompanyItem
                key={company.id}
                onClick={() => handleCompanySelect(company)}
              >
                <CompanyLogo 
                  src={company.logo} 
                  alt={company.name}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '🏢'; // Fallback emoji if logo fails to load
                  }}
                />
                <CompanyInfo>
                  <CompanyName>{company.name}</CompanyName>
                  <CompanyCategory>{company.category}</CompanyCategory>
                </CompanyInfo>
              </CompanyItem>
            ))}
          </CompanyList>
        </SuggestionsContainer>
      )}
    </SearchContainer>
  );
};

export default SearchBar; 